import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { t } from '@aquabox/shared';
import { api } from './api/client';
import { useDashboard, useMe } from './api/queries';
import { Layout } from './components/Layout';
import { Login } from './screens/Login';
import { ResetPassword } from './screens/ResetPassword';
import { Overview } from './screens/Overview';
import { Orders } from './screens/Orders';
import { DayBoard } from './screens/DayBoard';
import { Cash } from './screens/Cash';
import { Integrations } from './screens/Integrations';
import { Map } from './screens/Map';
import { Reports } from './screens/Reports';
import { Personnel } from './screens/Personnel';
import { Stock } from './screens/Stock';
import { Approvals } from './screens/Approvals';
import { Depot } from './screens/Depot';
import { Customers } from './screens/Customers';
import { Trips } from './screens/Trips';
import { Products } from './screens/Products';
import { Fleet } from './screens/Fleet';
import { Regions } from './screens/Regions';
import { Settings } from './screens/Settings';
import { Setup } from './screens/Setup';
import { PanelAssign } from './screens/PanelAssign';
import { PrintGate } from './screens/PrintQueue';
import { canPrint } from './lib/print-queue';

/** Gün Tahtası gizli (UX, 31.10); `VITE_DAYBOARD=1` ile açılır. Menüdeki bayrakla aynı. */
function dayBoardEnabled(): boolean {
  return import.meta.env.VITE_DAYBOARD === "1";
}

/**
 * Uygulama kabuğu: oturum, sürüm bandı ve erişim durumu bandı.
 *
 * Sürüm bandı (GUNCELLEME-VE-SURUM.md §6): panel 5 dakikada bir manifest sorar; sunucudaki sürüm
 * farklıysa üstte bant çıkar. Kırıcı değişiklikte (426) bant kapatılamaz ve panel kilitlenir.
 */
export function App() {
  const [authed, setAuthed] = useState(api.isAuthenticated);
  const { pathname } = useLocation();
  const [restoring, setRestoring] = useState(api.hasRefreshToken);
  const [upgradeBlocked, setUpgradeBlocked] = useState(false);
  const [newVersion, setNewVersion] = useState(false);

  useEffect(() => api.onAuthChange(() => setAuthed(api.isAuthenticated)), []);
  useEffect(() => api.onUpgradeRequired(() => setUpgradeBlocked(true)), []);

  // Sekme yenilendiğinde refresh token ile oturumu geri al.
  useEffect(() => {
    if (!api.hasRefreshToken) return;
    void api.restoreSession().finally(() => setRestoring(false));
  }, []);

  // Sürüm denetimi: 5 dakikada bir manifest. Form doluyken otomatik yenileme YAPILMAZ,
  // yalnızca bant gösterilir (§6).
  useEffect(() => {
    async function check(): Promise<void> {
      try {
        const res = await fetch('/api/app/manifest');
        if (!res.ok) return;
        const manifest = (await res.json()) as { app: { latestAppVersion: string } };
        if (manifest.app.latestAppVersion !== __APP_VERSION__) setNewVersion(true);
      } catch {
        // Ağ yoksa sessiz geç; bir sonraki turda tekrar denenir.
      }
    }
    void check();
    const timer = setInterval(() => void check(), 5 * 60_000);
    return () => clearInterval(timer);
  }, []);

  if (upgradeBlocked) {
    return (
      <div
        style={{
          padding: 40,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        <h1 className="h1">{t('version.upgradeRequired')}</h1>
        <p style={{ fontSize: 15 }}>{t('panel.upgradeBlocked')}</p>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          {t('panel.refresh')}
        </button>
      </div>
    );
  }

  if (restoring) {
    return <p style={{ padding: 40, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;
  }

  // Parola sıfırlama bağlantısı oturum açıkken de yutulmaz: aynı ekran gösterilir.
  if (authed && pathname === '/sifre-sifirla') return <ResetPassword />;

  if (!authed) {
    // Oturumsuz rotalar: parola sıfırlama bağlantısı login'siz açılır.
    return (
      <Routes>
        <Route path="/sifre-sifirla" element={<ResetPassword />} />
        <Route path="*" element={<LoggedOut onSuccess={() => setAuthed(true)} />} />
      </Routes>
    );
  }

  return <AuthedApp newVersion={newVersion} />;
}

/** Oturumsuz giriş ekranı; sıfırlama sonrası gelen başarı mesajını konum durumundan okur. */
function LoggedOut({ onSuccess }: { onSuccess: () => void }) {
  const state = useLocation().state as { notice?: string } | null;
  return <Login onSuccess={onSuccess} notice={state?.notice} />;
}

function AuthedApp({ newVersion }: { newVersion: boolean }) {
  const me = useMe();
  const dashboard = useDashboard();
  const canWrite = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const printAllowed = canPrint(me.data?.role);
  // Personel ekranı (kişi kartları, cihazlar, defter) yalnız OWNER/MANAGER; API de aynı kısıtı uygular.
  const personnelAllowed = canWrite;
  const accessStatus = dashboard.data?.tenant.status ?? 'ACTIVE';

  return (
    <Layout
      dashboard={dashboard.data}
      tenant={dashboard.data?.tenant}
      userName={me.data ? t(`role.${me.data.role}`) : ''}
      onLogout={() => void api.logout()}
      canPrint={printAllowed}
      canPersonnel={personnelAllowed}
      canDepot={canWrite}
    >
      {newVersion && (
        <div
          role="status"
          style={{
            background: 'var(--primary-bg)',
            borderBottom: '1px solid var(--primary-light)',
            padding: '10px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontWeight: 600,
            fontSize: 13.5,
            color: 'var(--primary)',
          }}
        >
          {t('panel.newVersionBanner')}
          <button
            className="btn btn-primary"
            style={{ padding: '6px 14px', fontSize: 13 }}
            onClick={() => window.location.reload()}
          >
            {t('panel.refresh')}
          </button>
        </div>
      )}

      {accessStatus !== 'ACTIVE' && (
        <div
          role="alert"
          style={{
            background: 'var(--bg-warning-soft)',
            borderBottom: '1px solid var(--warning)',
            padding: '10px 32px',
            fontWeight: 600,
            fontSize: 13.5,
            color: 'var(--warning)',
          }}
        >
          {accessStatus === 'READONLY' ? t('panel.readonlyBanner') : t('panel.suspendedBanner')}
        </div>
      )}

      <Routes>
        <Route
          path="/"
          element={
            dashboard.isPending ? (
              <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
            ) : dashboard.data ? (
              <Overview data={dashboard.data} />
            ) : (
              <div style={{ padding: 32 }}>
                <p style={{ marginBottom: 12 }}>{t('panel.loadFailed')}</p>
                <button className="btn btn-secondary" onClick={() => void dashboard.refetch()}>
                  {t('panel.retry')}
                </button>
              </div>
            )
          }
        />
        <Route
          path="/siparisler"
          element={<Orders canWrite={canWrite && accessStatus === 'ACTIVE'} />}
        />
        <Route
          path="/gun-tahtasi"
          element={
            dayBoardEnabled() ? (
              <DayBoard canWrite={canWrite && accessStatus === 'ACTIVE'} />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/musteriler" element={<Customers />} />
        <Route path="/kasa" element={<Cash />} />
        <Route path="/stok" element={<Stock />} />
        {/* Onaylar: yalnız OWNER/MANAGER (API de aynı kısıtı uygular: `/api/approvals`). */}
        <Route
          path="/onaylar"
          element={
            me.isPending ? (
              <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
            ) : canWrite ? (
              <Approvals />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {/* Depo günü: yalnız OWNER/MANAGER (API de aynı kısıtı uygular). Rol yüklenene kadar bekler. */}
        <Route
          path="/depo"
          element={
            me.isPending ? (
              <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
            ) : canWrite ? (
              <Depot />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/harita" element={<Map />} />
        <Route path="/entegrasyonlar" element={<Integrations />} />
        <Route path="/raporlar" element={<Reports />} />
        <Route path="/seferler" element={<Trips />} />
        {/* 21.10: Şoförler ve Ekip kaldırıldı; yer imleri yeni ekranlara yönlenir. */}
        <Route
          path="/personel"
          element={
            me.isPending ? (
              <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
            ) : personnelAllowed ? (
              <Personnel />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/soforler" element={<Navigate to="/personel?tab=performans" replace />} />
        <Route path="/urunler" element={<Products />} />
        <Route path="/araclar" element={<Fleet />} />
        <Route path="/ekip" element={<Navigate to="/araclar" replace />} />
        <Route path="/bolgeler" element={<Regions />} />
        <Route path="/ayarlar" element={<Settings />} />
        <Route path="/kurulum" element={<Setup />} />
        <Route path="/panel-atama" element={<PanelAssign />} />
        <Route path="/yazdir" element={<PrintGate />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
